import { getCurrencyByCode } from '@/constants/currencies'
import { fromCents, toCents } from '@lib/money'
import { getInitials, getShortName } from '@/utils/userNames'
import CurrencySelect from '@components/CurrencySelect'
import Modal from '@components/Modal'
import { GetPayUpType, GetUserType } from '@orbit/api-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    AmountInput,
    AmountInputWrapper,
    AmountPrefix,
    Button,
    ButtonGroup,
    ButtonGroupRight,
    CrossUserWarning,
    FileAddPlaceholder,
    FilePreview,
    FileRemoveButton,
    FilesSection,
    FormGroup,
    FormLabel,
    HiddenFileInput,
    ImageOverlay,
    ReadOnlyArrow,
    ReadOnlyAvatar,
    ReadOnlyHeader,
    ReadOnlyLabel,
    ReadOnlyName,
    ReadOnlyPerson,
    SectionLabel,
    SoftWarning,
    ValidationError,
} from './EditPayUpModal.styles'

const SOFT_WARN_THRESHOLD_CENTS = 100_000_00

export interface EditPayUpData {
    amount: number
    currencyCode: string
    newImageFiles: File[]
    deletedFileIds: string[]
}

interface EditPayUpModalProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (data: EditPayUpData) => void
    onDelete: () => void
    payUp: GetPayUpType | null
    members: GetUserType[]
    currentUserId: number
    fileUrlById: Map<string, string>
    isSubmitting?: boolean
}

const ReadOnlyPersonAvatar: React.FC<{ member: GetUserType | undefined }> = ({ member }) => (
    <ReadOnlyAvatar>
        {member?.profilePicture ? (
            <img src={member.profilePicture} alt={getInitials(member)} />
        ) : (
            getInitials(member)
        )}
    </ReadOnlyAvatar>
)

const EditPayUpModal: React.FC<EditPayUpModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    onDelete,
    payUp,
    members,
    currentUserId,
    fileUrlById,
    isSubmitting = false,
}) => {
    const [amount, setAmount] = useState('')
    const [currencyCode, setCurrencyCode] = useState('usd')
    const [newImageFiles, setNewImageFiles] = useState<File[]>([])
    const [newImagePreviewUrls, setNewImagePreviewUrls] = useState<string[]>([])
    const [deletedFileIds, setDeletedFileIds] = useState<string[]>([])
    const [overlayUrl, setOverlayUrl] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const newImagePreviewUrlsRef = useRef<string[]>([])

    useEffect(() => {
        newImagePreviewUrlsRef.current = newImagePreviewUrls
    }, [newImagePreviewUrls])

    useEffect(() => {
        if (!isOpen || !payUp) return
        setAmount(fromCents(payUp.amount))
        setCurrencyCode(payUp.currencyCode)
        setNewImageFiles([])
        setNewImagePreviewUrls(prev => {
            prev.forEach(URL.revokeObjectURL)
            return []
        })
        setDeletedFileIds([])
        setOverlayUrl(null)
    }, [isOpen, payUp])

    useEffect(() => {
        return () => {
            newImagePreviewUrlsRef.current.forEach(URL.revokeObjectURL)
        }
    }, [])

    const fromMember = useMemo(
        () => members.find(m => m.id === payUp?.fromUserId),
        [members, payUp],
    )
    const toMember = useMemo(
        () => members.find(m => m.id === payUp?.toUserId),
        [members, payUp],
    )

    const isCurrentUserParticipant =
        payUp != null &&
        (payUp.fromUserId === currentUserId || payUp.toUserId === currentUserId)
    const showCrossUserWarning = payUp != null && !isCurrentUserParticipant

    const amountCents = toCents(amount)
    const currencyInfo = getCurrencyByCode(currencyCode)
    const symbol = currencyInfo?.symbol ?? '$'
    const showSoftWarning = amountCents > SOFT_WARN_THRESHOLD_CENTS

    const validationError = (() => {
        if (amountCents <= 0) return 'Amount must be greater than zero'
        return null
    })()

    const canSubmit = !validationError && !isSubmitting

    const remainingExistingFileIds = useMemo(() => {
        return (payUp?.fileIds ?? []).filter(id => !deletedFileIds.includes(id))
    }, [payUp, deletedFileIds])

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setNewImageFiles(prev => [...prev, file])
        setNewImagePreviewUrls(prev => [...prev, URL.createObjectURL(file)])
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleRemoveExistingFile = (fileId: string) => {
        setDeletedFileIds(prev => (prev.includes(fileId) ? prev : [...prev, fileId]))
    }

    const handleRemoveNewFile = (idx: number) => {
        setNewImageFiles(prev => prev.filter((_, i) => i !== idx))
        setNewImagePreviewUrls(prev => {
            const url = prev[idx]
            if (url) URL.revokeObjectURL(url)
            return prev.filter((_, i) => i !== idx)
        })
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return
        onSubmit({
            amount: amountCents,
            currencyCode,
            newImageFiles,
            deletedFileIds,
        })
    }

    if (!payUp) return null

    const formId = 'edit-payup-form'

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Payup"
            size="medium"
            backgroundColor="#fff8f6"
            footer={
                <ButtonGroup>
                    {isCurrentUserParticipant ? (
                        <Button type="button" $variant="danger" onClick={onDelete}>
                            Delete
                        </Button>
                    ) : (
                        <span />
                    )}
                    <ButtonGroupRight>
                        <Button type="button" $variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            form={formId}
                            $variant="primary"
                            disabled={!canSubmit}
                        >
                            {isSubmitting ? 'Saving...' : 'Save changes'}
                        </Button>
                    </ButtonGroupRight>
                </ButtonGroup>
            }
        >
            {showCrossUserWarning && (
                <CrossUserWarning>
                    This payup is between <strong>{getShortName(fromMember)}</strong> and{' '}
                    <strong>{getShortName(toMember)}</strong> — edits will affect their balances.
                </CrossUserWarning>
            )}

            <ReadOnlyHeader>
                <ReadOnlyPerson>
                    <ReadOnlyLabel>From</ReadOnlyLabel>
                    <ReadOnlyPersonAvatar member={fromMember} />
                    <ReadOnlyName>{getShortName(fromMember)}</ReadOnlyName>
                </ReadOnlyPerson>
                <ReadOnlyArrow>→</ReadOnlyArrow>
                <ReadOnlyPerson>
                    <ReadOnlyLabel>To</ReadOnlyLabel>
                    <ReadOnlyPersonAvatar member={toMember} />
                    <ReadOnlyName>{getShortName(toMember)}</ReadOnlyName>
                </ReadOnlyPerson>
            </ReadOnlyHeader>

            <form id={formId} onSubmit={handleSubmit}>
                <FormGroup>
                    <FormLabel htmlFor="edit-payup-amount">Amount</FormLabel>
                    <AmountInputWrapper>
                        <AmountPrefix>{symbol}</AmountPrefix>
                        <AmountInput
                            id="edit-payup-amount"
                            type="number"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            placeholder="0.00"
                            min="0.01"
                            step="0.01"
                            autoFocus
                        />
                    </AmountInputWrapper>
                    {showSoftWarning && (
                        <SoftWarning>That seems high — please confirm.</SoftWarning>
                    )}
                    {validationError && amountCents <= 0 && amount !== '' && (
                        <ValidationError>{validationError}</ValidationError>
                    )}
                </FormGroup>

                <FormGroup>
                    <CurrencySelect
                        value={currencyCode}
                        onChange={setCurrencyCode}
                        label="Currency"
                        htmlFor="edit-payup-currency"
                    />
                </FormGroup>

                <FormGroup>
                    <SectionLabel>Receipts</SectionLabel>
                    <HiddenFileInput
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                    />
                    <FilesSection>
                        {remainingExistingFileIds.map(id => {
                            const url = fileUrlById.get(id)
                            if (!url) return null
                            return (
                                <FilePreview key={id}>
                                    <img
                                        src={url}
                                        alt="Receipt"
                                        onClick={() => setOverlayUrl(url)}
                                    />
                                    <FileRemoveButton
                                        type="button"
                                        onClick={() => handleRemoveExistingFile(id)}
                                        aria-label="Remove receipt"
                                    >
                                        ×
                                    </FileRemoveButton>
                                </FilePreview>
                            )
                        })}
                        {newImagePreviewUrls.map((url, idx) => (
                            <FilePreview key={`new-${idx}`}>
                                <img
                                    src={url}
                                    alt="New receipt"
                                    onClick={() => setOverlayUrl(url)}
                                />
                                <FileRemoveButton
                                    type="button"
                                    onClick={() => handleRemoveNewFile(idx)}
                                    aria-label="Remove receipt"
                                >
                                    ×
                                </FileRemoveButton>
                            </FilePreview>
                        ))}
                        <FileAddPlaceholder onClick={() => fileInputRef.current?.click()}>
                            +
                        </FileAddPlaceholder>
                    </FilesSection>
                </FormGroup>
            </form>

            {overlayUrl && (
                <ImageOverlay onClick={() => setOverlayUrl(null)}>
                    <img src={overlayUrl} alt="Receipt full size" />
                </ImageOverlay>
            )}
        </Modal>
    )
}

export default EditPayUpModal
